import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';

export type Relationship =
  | 'daughter'
  | 'son'
  | 'spouse'
  | 'parent'
  | 'sibling'
  | 'other';

interface RelationshipBadgeProps {
  relationship: Relationship | string | null | undefined;
  className?: string;
}

const COLOR_BY_RELATIONSHIP: Record<Relationship, string> = {
  daughter: 'bg-rose-100 text-rose-900 border-rose-200',
  son: 'bg-sky-100 text-sky-900 border-sky-200',
  spouse: 'bg-mint-100 text-mint-900 border-mint-200',
  parent: 'bg-peach-100 text-peach-900 border-peach-200',
  sibling: 'bg-lavender-100 text-lavender-900 border-lavender-200',
  other: 'bg-ink-100 text-ink-700 border-ink-200',
};

/**
 * Cluster H — small badge displayed at the top of SubScreeningDrawer
 * indicating the household member's relationship to the parent client.
 *
 * Color coding aims to read at a glance from across the screen: rose/sky
 * for children, mint for spouse, peach for the older generation, lavender
 * for siblings, ink-gray for "other". Same palette family as the rest
 * of Pathway's design system so the badge fits the surrounding chrome.
 *
 * If the input is null/undefined or an unknown string, falls back to
 * 'other' coloring with the literal text echoed (preserves whatever the
 * household-members capture form recorded).
 */
export function RelationshipBadge({
  relationship,
  className,
}: RelationshipBadgeProps): JSX.Element {
  const t = useT();
  const lang = useLang();

  const normalized = (relationship ?? 'other').toLowerCase() as Relationship;
  const known: Relationship = (
    ['daughter', 'son', 'spouse', 'parent', 'sibling', 'other'] as const
  ).includes(normalized)
    ? normalized
    : 'other';

  const label =
    known === 'other' && relationship && relationship !== 'other'
      ? relationship
      : t(`subScreening.relationship.${known}` as never);

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide border',
        COLOR_BY_RELATIONSHIP[known],
        className,
      )}
      title={lang === 'es' ? 'Relación' : 'Relationship'}
    >
      {label}
    </span>
  );
}
